<template>
  <div class="space-y-2 flex flex-col justify-between h-full">
    <div>
      <label for="join-room-code" class="block text-sm font-medium text-text-secondary mb-1">Room Code</label>
      <input
        id="join-room-code"
        v-model="roomCode"
        name="roomCode"
        type="text"
        placeholder="Enter 6-character code"
        maxlength="6"
        class="w-full bg-background border border-text-secondary/20 rounded-xl px-4 py-3 text-text-primary text-center tracking-widest text-xl uppercase focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
        @input="roomCode = roomCode.toUpperCase()"
        @keyup.enter="handleJoin"
      />
    </div>

    <div class="basis-full">
      <label for="join-display-name" class="block text-sm font-medium text-text-secondary mb-1">Your Display Name</label>
      <input
        id="join-display-name"
        v-model="displayName"
        name="displayName"
        type="text"
        placeholder="Enter your name"
        class="w-full bg-background border border-text-secondary/20 rounded-xl px-4 py-3 text-text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
        @keyup.enter="handleJoin"
      />
    </div>

    <button
      class="w-full bg-primary text-text-primary rounded-xl py-3 px-4 font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      :disabled="loading || !canJoin"
      @click="handleJoin"
    >
      <span v-if="loading" class="flex items-center justify-center gap-2">
        <svg class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Joining...
      </span>
      <span v-else>Join Room</span>
    </button>

    <p v-if="error" class="text-error text-sm text-center">{{ error }}</p>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useMultiplayerRoom } from '@/composables/useMultiplayerRoom';
import { useToast } from '@/composables/useToast';
import { useConvexQuery } from 'convex-vue';
import { api } from '../../../convex/_generated/api';

const emit = defineEmits<{
  (e: 'joined', roomCode: string, roomStatus?: string): void;
}>();

const { joinRoom, room } = useMultiplayerRoom();
const { createToast } = useToast();
const { data: currentUser } = useConvexQuery(api.users.getCurrentUser, {});
const roomCode = ref('');
const displayName = ref('');
const loading = ref(false);
const error = ref('');

watch(currentUser, (user) => {
  if (user?.displayName && !displayName.value) {
    displayName.value = user.displayName;
  }
}, { immediate: true });

const canJoin = computed(() => roomCode.value.length === 6 && displayName.value.trim().length > 0);

const handleJoin = async () => {
  if (!canJoin.value) return;

  loading.value = true;
  error.value = '';

  try {
    await joinRoom({
      code: roomCode.value,
      displayName: displayName.value.trim(),
    });

    createToast({
      type: 'success',
      message: 'Joined room successfully!',
    });

    emit('joined', roomCode.value, room.value?.status);
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to join room';
    createToast({
      type: 'error',
      message: error.value,
    });
  } finally {
    loading.value = false;
  }
};
</script>
